import { useEffect, useState } from 'react';
import CustomModalOverlay from '../../../../common/Custom/CustomModalOverlay/CustomModalOverlay';
import GlobalButton from '../../../../ui/GlobalButton';
import TextInput from '../../../../ui/TextInput/TextInput';
import { useForm } from '@mantine/form';
import WebAppService from '../../../../../services/WebAppService';
import SelectInput from '../../../../ui/SelectInput/SelectInput';
import {
  Amplitude,
  Confluence,
  Figma,
  Github,
  GoogleDocs,
  GoogleDrive,
  Notion,
  OtherIcon,
  Slack,
} from '../../../../../svgs/logos';
import { useLinkValidation } from '../useLinkValidation';
import type { Task } from '@/types';

interface AddLinkModalProps {
  open: boolean;
  close: () => void;
  task: Task;
  refetch: () => Promise<void>;
}

export const LINK_TYPE_OPTIONS = [
  { label: 'Google Docs', value: 'Google Docs', icon: <GoogleDocs /> },
  { label: 'Google Drive', value: 'Google Drive', icon: <GoogleDrive /> },
  { label: 'Confluence', value: 'Confluence', icon: <Confluence /> },
  { label: 'Notion', value: 'Notion', icon: <Notion /> },
  { label: 'Figma', value: 'Figma', icon: <Figma /> },
  { label: 'Github', value: 'GitHub', icon: <Github /> },
  { label: 'Amplitude', value: 'Amplitude', icon: <Amplitude /> },
  { label: 'Slack', value: 'Slack', icon: <Slack /> },
  { label: 'Other', value: 'Other', icon: <OtherIcon size={16} /> },
];

export default function AddLinkModal({
  open,
  close,
  task,
  refetch,
}: AddLinkModalProps) {
  const [loading, setLoading] = useState(false);

  
  const form = useForm({
    initialValues: {
      title: '',
      task_link_url: '',
      task_link_type: '',
    },
  });

  const { detectedType } = useLinkValidation(form.values.task_link_url);

  useEffect(() => {
    if (detectedType) {
      form.setFieldValue('task_link_type', detectedType);
    }
  }, [detectedType]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await WebAppService.createTaskLink(task?._id!, {
        task_link_url: form.values.task_link_url,
      });
      await refetch();
      form.reset();
    } catch (error) {
      console.error('Error adding link:', error);
    } finally {
      setLoading(false);
      close();
    }
  };
  return (
    <CustomModalOverlay
      open={open}
      onClose={() => {
        close();
        form.reset();
      }}
      title={'Add Link to Task'}
      subTitle={
        'Add a link that helps others understand or follow this task easily. Collaboration works best when information is shared.'
      }
      width="600px"
    >
      <div>
        <form className="flex flex-col gap-4">
          <TextInput
            label={'Enter Link Title'}
            placeholder={'Enter link title '}
            {...form.getInputProps('title')}
          />

          <TextInput
            label={'Enter URL'}
            placeholder={'Enter a valid url'}
            {...form.getInputProps('task_link_url')}
          />

          <SelectInput
            label={'Link Type'}
            placeholder="Select the link type"
            {...form.getInputProps('task_link_type')}
            options={LINK_TYPE_OPTIONS}
          />
        </form>

        <div className="mt-6 mb-4 flex items-center justify-end gap-4">
          <div>
            <GlobalButton
              variant="stroke"
              onClick={() => {
                close();
                form.reset();
              }}
            >
              Cancel
            </GlobalButton>
          </div>
          <div>
            <GlobalButton
              onClick={handleSubmit}
              type={'submit'}
              state={loading ? 'inactive' : 'default'}
            >
              Save Link
            </GlobalButton>
          </div>
        </div>
      </div>
    </CustomModalOverlay>
  );
}
